<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { AdsModal, AdsButton, AdsMessage } from '@accor/ads-components'
import { CcfMarkdownText } from '@accor/customer-vue-components'
import { storeToRefs } from 'pinia'
import { useEmployeeStore } from '@/stores/employee'
import { useUserStore } from '@/stores/user'
import { COLLABORATOR_STATUS } from '@/constants/collaborator'
import { acceptTermsAndConditions } from '@/services/employees'
import { logger } from '@/services/logger'

const { t } = useI18n()
const employeeStore = useEmployeeStore()
const userStore = useUserStore()
const { employee, hasAcceptedTerms } = storeToRefs(employeeStore)

const isLoading = ref(false)
const hasError = ref(false)

const firstName = computed(() => employee.value?.personalInfo.firstName ?? '')
const lastName = computed(() => employee.value?.personalInfo.lastName ?? '')
const role = computed(() => employee.value?.authorization.role ?? COLLABORATOR_STATUS.EMPLOYEE)
const shouldDisplayModal = computed(
  () => userStore.isAab && !!employee.value && !hasAcceptedTerms.value
)

const newRoleDisplayName = computed(() =>
  role.value === COLLABORATOR_STATUS.ADMINISTRATOR
    ? t('aab.company.dashboard.admin.count.label', 1)
    : t('aab.company.dashboard.traveler.count.label', 1)
)

const bodyText = computed(() => {
  return role.value === COLLABORATOR_STATUS.ADMINISTRATOR
    ? t('aab.termsAndConditions.acceptance.modale.body.administrator')
    : t('aab.termsAndConditions.acceptance.modale.body.traveller')
})
const legalMentionText = computed(() => {
  return role.value === COLLABORATOR_STATUS.ADMINISTRATOR
    ? t('aab.termsAndConditions.acceptance.modale.legalMention.administrator')
    : t('aab.termsAndConditions.acceptance.modale.legalMention.traveller')
})

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (isLoading.value) {
    event.preventDefault()
    return ''
  }
}

const onAcceptTerms = async () => {
  try {
    isLoading.value = true
    hasError.value = false
    window.addEventListener('beforeunload', handleBeforeUnload)

    await acceptTermsAndConditions()

    window.removeEventListener('beforeunload', handleBeforeUnload)
    window.location.reload()
  } catch (error) {
    logger.error(error)
    hasError.value = true
  } finally {
    window.removeEventListener('beforeunload', handleBeforeUnload)
    isLoading.value = false
  }
}

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})
</script>

<template>
  <AdsModal
    v-model:is-visible="shouldDisplayModal"
    position="center"
    :allow-closing-on-backdrop="false"
    :hide-dismiss-button="true"
    initial-focus="autofocus"
    class="terms-acceptance-modal"
    data-testid="terms-acceptance-modal"
  >
    <template #container>
      <div class="terms-acceptance-modal__body">
        <h2 class="terms-acceptance-modal__title">
          {{ t('aab.termsAndConditions.acceptance.modale.title') }}
        </h2>
        <CcfMarkdownText
          class="terms-acceptance-modal__new-role"
          :text="
            t('aab.termsAndConditions.acceptance.modale.newRole', {
              FirstName: firstName,
              LastName: lastName,
              NewRole: newRoleDisplayName
            })
          "
        />
        <CcfMarkdownText :text="bodyText" />
        <CcfMarkdownText
          class="terms-acceptance-modal__legal-mention"
          target="_blank"
          :text="legalMentionText"
        />
        <AdsMessage
          v-if="hasError"
          variant="danger"
          :message="t('aab.generic.error.message')"
          data-testid="terms-acceptance-error"
        />
        <AdsButton
          class="terms-acceptance-modal__cta"
          variant="primary"
          :is-loading="isLoading"
          :disabled="isLoading"
          @click="onAcceptTerms"
        >
          {{ t('aab.generic.continue.cta') }}
        </AdsButton>
      </div>
    </template>
  </AdsModal>
</template>

<style lang="scss" scoped>
.terms-acceptance-modal {
  &__body {
    display: flex;
    flex-direction: column;
    gap: ads.$ads-spacing-scale-lg;
    padding: ads.$ads-spacing-scale-2xl;

    :deep(ul) {
      list-style: disc;
      padding-left: ads.$ads-spacing-scale-lg;
    }
  }

  &__title {
    @include ads-sass.display-05;
  }

  &__cta {
    align-self: center;
    margin-top: ads.$ads-spacing-scale-md;
  }
}
</style>
